<script setup>
import { computed, onMounted } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { dynamicTime } from 'shared/helpers/timeHelper';

import BroadcastLayout from 'dashboard/components-next/broadcasts/BroadcastLayout.vue';
import StatusBadge from 'dashboard/components-next/broadcasts/StatusBadge.vue';

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const uiFlags = useMapGetter('broadcasts/getUIFlags');
const broadcasts = useMapGetter('broadcasts/getRecords');
const meta = useMapGetter('broadcasts/getMeta');
const isFetching = computed(() => uiFlags.value.fetchingList);

const CHANNEL_KEYS = {
  telegram: 'BROADCASTS.CHANNELS.TELEGRAM',
  max: 'BROADCASTS.CHANNELS.MAX',
  vk: 'BROADCASTS.CHANNELS.VK',
  email: 'BROADCASTS.CHANNELS.EMAIL',
  website: 'BROADCASTS.CHANNELS.WEBSITE',
};

const audienceSummary = broadcast => {
  const channels = broadcast.audience?.channels || [];
  if (!channels.length) return t('BROADCASTS.LIST.AUDIENCE_ANY');
  return channels
    .map(channel =>
      CHANNEL_KEYS[channel] ? t(CHANNEL_KEYS[channel]) : channel
    )
    .join(', ');
};

const formatDate = broadcast => {
  const timestamp = broadcast.scheduled_at || broadcast.created_at;
  return timestamp ? dynamicTime(timestamp) : '—';
};

const fetchBroadcasts = (page = 1) => {
  store.dispatch('broadcasts/get', { page });
};

const onPageChange = page => fetchBroadcasts(page);

const handleCreate = () => {
  router.push({ name: 'broadcasts_new' });
};

const openBroadcast = broadcast => {
  router.push({
    name: 'broadcasts_details',
    params: { broadcastId: broadcast.id },
  });
};

onMounted(() => fetchBroadcasts());
</script>

<template>
  <BroadcastLayout
    :header-title="t('BROADCASTS.LIST.HEADER')"
    :button-label="t('BROADCASTS.LIST.ADD_NEW')"
    :is-fetching="isFetching"
    :is-empty="!broadcasts.length"
    :show-pagination-footer="!isFetching && !!broadcasts.length"
    :current-page="meta.page"
    :total-count="meta.totalCount"
    @click="handleCreate"
    @update:current-page="onPageChange"
  >
    <template #emptyState>
      <span class="text-base font-medium text-n-slate-12">
        {{ t('BROADCASTS.LIST.EMPTY_TITLE') }}
      </span>
      <span class="max-w-md text-sm text-n-slate-11">
        {{ t('BROADCASTS.LIST.EMPTY_SUBTITLE') }}
      </span>
    </template>

    <template #body>
      <div class="overflow-x-auto">
        <table class="w-full text-sm border-collapse">
          <thead>
            <tr class="border-b border-n-weak text-n-slate-11">
              <th class="px-3 py-2 font-medium text-start">
                {{ t('BROADCASTS.LIST.TABLE.TITLE') }}
              </th>
              <th class="px-3 py-2 font-medium text-start">
                {{ t('BROADCASTS.LIST.TABLE.STATUS') }}
              </th>
              <th class="px-3 py-2 font-medium text-start">
                {{ t('BROADCASTS.LIST.TABLE.AUDIENCE') }}
              </th>
              <th class="px-3 py-2 font-medium text-start">
                {{ t('BROADCASTS.LIST.TABLE.DATE') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="broadcast in broadcasts"
              :key="broadcast.id"
              class="border-b border-n-weak last:border-b-0 cursor-pointer hover:bg-n-alpha-1"
              @click="openBroadcast(broadcast)"
            >
              <td class="px-3 py-2.5 text-n-slate-12 font-medium">
                {{ broadcast.title || t('BROADCASTS.LIST.UNTITLED') }}
              </td>
              <td class="px-3 py-2.5">
                <StatusBadge :status="broadcast.status" />
              </td>
              <td class="px-3 py-2.5 text-n-slate-11">
                {{ audienceSummary(broadcast) }}
              </td>
              <td class="px-3 py-2.5 text-n-slate-11">
                {{ formatDate(broadcast) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </BroadcastLayout>
</template>
